
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const AnimatedCounter: React.FC<{ end: number; duration?: number; suffix?: string }> = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const elementRef = useRef<HTMLSpanElement>(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasStarted.current) {
          hasStarted.current = true;
          let startTime: number | null = null;
          
          const step = (timestamp: number) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            setCount(Math.floor(progress * end));
            if (progress < 1) {
              window.requestAnimationFrame(step);
            }
          };
          
          window.requestAnimationFrame(step);
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) observer.observe(elementRef.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={elementRef}>{count}{suffix}</span>;
};

const ValueCard: React.FC<{ number: string; title: string; desc: string; icon: string }> = ({ number, title, desc, icon }) => (
  <div className="group relative bg-white/70 backdrop-blur-md border border-white/50 p-8 md:p-10 rounded-lg shadow-[0_20px_50px_rgba(5,7,60,0.05)] hover:shadow-[0_30px_70px_rgba(255,130,46,0.15)] transition-all duration-500 hover:-translate-y-2 overflow-hidden">
    <div className="absolute -top-4 -right-4 text-[120px] font-black text-brand-deep/5 group-hover:text-brand-accent/10 transition-colors leading-none pointer-events-none select-none">
      {number}
    </div>
    
    <div className="relative z-10">
      <div className="w-14 h-14 bg-brand-accent text-white rounded-lg flex items-center justify-center text-2xl mb-8 shadow-lg shadow-orange-200 group-hover:scale-110 transition-transform duration-500">
        <i className={icon}></i>
      </div>
      <h4 className="text-2xl font-black text-brand-deep mb-4 tracking-tight group-hover:text-brand-accent transition-colors">
        {title}
      </h4>
      <p className="text-brand-deep/70 font-medium leading-relaxed">
        {desc}
      </p>
    </div>
    <div className="absolute bottom-0 left-0 h-1 w-0 bg-brand-accent group-hover:w-full transition-all duration-700"></div>
  </div>
);

export const ParMums: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-white relative overflow-hidden">
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-[20%] -left-[10%] w-[600px] h-[600px] bg-brand-accent/5 rounded-full blob-bg animate-float"></div>
        <div className="absolute bottom-[10%] -right-[10%] w-[500px] h-[500px] bg-brand-deep/5 rounded-full blob-bg animate-float" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative z-10">
        {/* Cinematic Hero Section */}
        <section className="relative h-[600px] md:h-[750px] flex items-center justify-center overflow-hidden mb-24">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1488085061387-422e29b40080?q=80&w=2400&auto=format&fit=crop" 
              className="w-full h-full object-cover brightness-[0.35] scale-105"
              alt="World Travel"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-brand-deep/40 via-transparent to-brand-deep/80"></div>
            <div className="absolute inset-0 bg-pattern-dots opacity-20"></div>
          </div>
          
          <div className="relative z-10 text-center px-6 w-full max-w-[1400px]">
            <div className="inline-block bg-brand-accent text-white px-6 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-[0.3em] mb-8 animate-fade-in shadow-xl shadow-orange-500/20">
              Kopš 2014. gada
            </div>
            
            <h1 className="text-5xl md:text-7xl font-black mb-8 text-white tracking-tighter leading-tight animate-fade-in uppercase">
              Mēs rakstām <br/>
              <span className="text-brand-accent italic drop-shadow-2xl">atmiņas.</span>
            </h1>
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-12 mt-16 opacity-0 animate-fade-in [animation-delay:400ms] [animation-fill-mode:forwards]">
              <p className="text-white/80 text-lg md:text-xl font-bold max-w-2xl text-center md:text-left leading-relaxed">
                LecTravel nav tikai tūrisma aģentūra. Mēs esam arhitekti jūsu drosmīgākajiem sapņiem, pārvēršot nezināmo par neaizmirstamo.
              </p>
              <div className="w-[1px] h-20 bg-white/20 hidden md:block"></div>
              <button 
                onClick={() => navigate('/kontakti')}
                className="group flex items-center gap-4 bg-brand-accent text-white px-10 py-5 rounded-lg font-black text-xs uppercase tracking-[0.2em] hover:bg-white hover:text-brand-deep transition-all shadow-2xl shadow-orange-500/20"
              >
                Sākt savu stāstu
                <i className="fas fa-arrow-right group-hover:translate-x-2 transition-transform"></i>
              </button>
            </div>
          </div>

          <div className="absolute bottom-12 right-12 hidden lg:flex flex-col items-end animate-float">
            <div className="bg-white/95 backdrop-blur-xl p-8 rounded-lg shadow-2xl border border-white/50 text-right">
              <div className="text-brand-accent text-3xl font-black mb-1">150k+</div>
              <div className="text-brand-deep/40 text-[9px] font-black uppercase tracking-widest">Nolidoti kilometri gadā</div>
            </div>
          </div>
        </section>

        {/* Brand Philosophy Section */}
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 relative mb-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
            <div className="relative group">
              <div className="absolute -top-10 -left-10 w-64 h-64 bg-brand-accent/10 rounded-lg -z-10 animate-pulse-slow"></div>
              <div className="absolute -bottom-10 -right-10 w-80 h-80 bg-brand-deep/5 rounded-lg -z-10"></div>
              <div className="relative rounded-lg overflow-hidden shadow-[0_50px_100px_rgba(5,7,60,0.15)] border-[12px] border-white ring-1 ring-gray-100 bg-white transform -rotate-1 group-hover:rotate-0 transition-transform duration-700">
                <img 
                  src="https://images.unsplash.com/photo-1503220317375-aaad61436b1b?q=80&w=1400&auto=format&fit=crop" 
                  alt="Exploration" 
                  className="w-full aspect-[4/5] object-cover" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-deep/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              </div>
              <div className="absolute -bottom-6 -left-6 md:-left-12 bg-brand-deep text-white p-10 rounded-lg shadow-2xl max-w-xs transform rotate-3">
                <i className="fas fa-quote-left text-brand-accent text-3xl mb-4 block"></i>
                <p className="font-black text-lg leading-tight tracking-tight italic text-white">
                  "Mūsu mērķis ir kļūt par jūsu ilgtermiņa partneri ceļojumu pasaulē."
                </p>
              </div>
            </div>

            <div className="space-y-10">
              <div>
                <div className="inline-block text-brand-accent font-black text-[10px] uppercase tracking-[0.4em] mb-6">Mūsu Manifests</div>
                <h2 className="text-5xl md:text-7xl font-black mb-8 text-brand-deep tracking-tighter leading-[0.9]">
                  Atklāj pasauli <br/> 
                  <span className="text-brand-accent">no jauna.</span>
                </h2>
              </div>
              
              <div className="space-y-6 text-xl text-brand-deep/70 font-medium leading-relaxed">
                <p>
                  Mēs neticam standarta katalogu tūrēm. Mēs ticam rītiem, kad saule aust virs nepazīstamas horizonta līnijas, un naktīm, kurās zvaigznes šķiet sasniedzamas.
                </p>
                <p>
                  Ar vairāk nekā 10 gadu pieredzi, mēs esam iemācījušies, ka vissvarīgākais ceļojumā ir miers. Tāpēc mēs uzņemamies visu loģistiku, lai jūs varētu vienkārši <span className="text-brand-deep font-black underline decoration-brand-accent decoration-4 underline-offset-4">būt.</span>
                </p>
              </div>

              <div className="grid grid-cols-2 gap-12 pt-8">
                <div className="border-l-4 border-brand-accent pl-6">
                  <div className="text-4xl font-black text-brand-deep mb-1 tracking-tighter">100%</div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-brand-deep/30">Individuāla pieeja</div>
                </div>
                <div className="border-l-4 border-brand-accent pl-6">
                  <div className="text-4xl font-black text-brand-deep mb-1 tracking-tighter">24/7</div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-brand-deep/30">Ekspertu atbalsts</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section - Standard Light Gray Background */}
        <section className="bg-gray-50 py-24 md:py-32 text-brand-deep">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-32">
            {/* Stat 01 */}
            <div className="space-y-10 group">
              <div className="text-[14px] font-bold text-brand-deep/10 tracking-widest">01</div>
              <h2 className="text-5xl md:text-7xl font-black tracking-tight leading-none text-brand-deep">
                <AnimatedCounter end={10} /> Gadus <br/> Tirgū
              </h2>
              <div className="space-y-8">
                <p className="text-brand-deep/60 text-lg md:text-xl font-medium leading-relaxed">
                  LEC Travel dibināšanas datums — 2014. gada jūnijs.
                </p>
                <p className="text-brand-deep/60 text-lg md:text-xl font-medium leading-relaxed">
                  Mums ir 100 procentu apmierinātu klientu. Ik gadu klientu skaits eksponenciāli pieaug. Pateicoties kvalitatīvam darbam un nevainojamam servisam.
                </p>
              </div>
              <button 
                onClick={() => navigate('/kontakti')}
                className="inline-flex items-center gap-4 px-8 py-4 border-2 border-brand-deep/20 rounded-lg font-black text-xs uppercase tracking-[0.2em] hover:bg-brand-deep hover:text-white hover:border-brand-deep transition-all group/btn"
              >
                Sazinies ar mums
                <i className="fas fa-arrow-right group-hover/btn:translate-x-2 transition-transform"></i>
              </button>
            </div>

            {/* Stat 02 */}
            <div className="space-y-10 group">
              <div className="text-[14px] font-bold text-brand-deep/10 tracking-widest">02</div>
              <h2 className="text-5xl md:text-7xl font-black tracking-tight leading-none text-brand-deep">
                <AnimatedCounter end={15} suffix="k+" /> <br/> Klientu
              </h2>
              <div className="space-y-8">
                <p className="text-brand-deep/60 text-lg md:text-xl font-medium leading-relaxed">
                  Nodrošināto galamērķu saraksts tiek pastāvīgi papildināts.
                </p>
                <p className="text-brand-deep/60 text-lg md:text-xl font-medium leading-relaxed">
                  Vēršoties pie mums, jūs saņemsiet kvalitatīvu apkalpošanu un individuālu pieeju, kā arī vērtīgus ieteikumus no mūsu aģentūras profesionāļiem.
                </p>
              </div>
              <button 
                onClick={() => navigate('/kontakti')}
                className="inline-flex items-center gap-4 px-8 py-4 border-2 border-brand-deep/20 rounded-lg font-black text-xs uppercase tracking-[0.2em] hover:bg-brand-deep hover:text-white hover:border-brand-deep transition-all group/btn"
              >
                Sazinies ar mums
                <i className="fas fa-arrow-right group-hover/btn:translate-x-2 transition-transform"></i>
              </button>
            </div>
          </div>
        </section>

        {/* Why Us Features Section */}
        <section className="py-32 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 relative">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <div className="inline-block text-brand-accent font-black text-[10px] uppercase tracking-[0.4em] mb-4">Kāpēc LecTravel</div>
            <h2 className="text-4xl md:text-6xl font-black text-brand-deep tracking-tighter leading-none mb-6">
              Mēs radām <span className="text-brand-accent">atšķirību</span>
            </h2>
            <p className="text-brand-deep/60 text-lg font-bold">Trīs pīlāri, uz kuriem balstās katra mūsu plānotā ekspedīcija.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            <ValueCard 
              number="01"
              title="Personalizācija"
              icon="fas fa-fingerprint"
              desc="Nekādu 'copy-paste' maršrutu. Katrs brauciens tiek modelēts atbilstoši jūsu personībai un vēlmēm."
            />
            <ValueCard 
              number="02"
              title="Drošība"
              icon="fas fa-shield-alt"
              desc="Mēs sadarbojamies tikai ar sertificētiem partneriem, nodrošinot augstāko drošības standartu visā pasaulē."
            />
            <ValueCard 
              number="03"
              title="Caurspīdīgums"
              icon="fas fa-eye"
              desc="Bez slēptām izmaksām. Jūs precīzi zināt, par ko maksājat, saņemot maksimālo vērtību par savu budžetu."
            />
          </div>
        </section>

        {/* Impact Visual Section */}
        <section className="py-24 bg-brand-deep relative overflow-hidden">
          <div className="absolute inset-0">
             <img 
               src="https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?q=80&w=2400&auto=format&fit=crop" 
               className="w-full h-full object-cover opacity-20 grayscale"
               alt="Inspiring landscape"
             />
             <div className="absolute inset-0 bg-gradient-to-r from-brand-deep via-brand-deep/80 to-transparent"></div>
          </div>
          
          <div className="max-w-[1400px] mx-auto px-6 relative z-10 flex flex-col lg:flex-row items-center gap-16 lg:gap-32">
             <div className="flex-1">
                <h2 className="text-4xl md:text-6xl font-black text-white tracking-tighter leading-tight mb-8">
                  Pievienojies mūsu <br/>
                  <span className="text-brand-accent">ceļotāju kopienai.</span>
                </h2>
                <p className="text-white/60 text-xl font-medium leading-relaxed mb-12">
                  Pasaule ir pārāk skaista, lai to redzētu tikai caur ekrānu. Ļauj mums būt tavam gidam uz vietām, kuras vēl neesi uzdrīkstējies iedomāties.
                </p>
                <div className="flex flex-wrap gap-8 items-center">
                   <div className="flex -space-x-4">
                     {[1,2,3,4,5].map(i => (
                       <img key={i} src={`https://i.pravatar.cc/100?u=user${i}`} className="w-14 h-14 rounded-lg border-4 border-brand-deep object-cover shadow-2xl" alt="Happy traveler" />
                     ))}
                   </div>
                   <div className="text-white/40 text-xs font-black uppercase tracking-widest">
                     Vairāk nekā <span className="text-white">15,000</span> stāstu
                   </div>
                </div>
             </div>
             
             <div className="flex-1 w-full max-w-lg">
                <div className="bg-white p-12 rounded-xl shadow-2xl border border-white/20 transform lg:rotate-2">
                   <div className="text-brand-accent text-5xl mb-8"><i className="fas fa-paper-plane"></i></div>
                   <h3 className="text-3xl font-black text-brand-deep mb-4 tracking-tight">Gatavi plānot?</h3>
                   <p className="text-brand-deep/60 font-bold mb-10">Mūsu konsultācija ir pirmais solis uz tavu labāko atvaļinājumu.</p>
                   <button 
                     onClick={() => navigate('/kontakti')}
                     className="w-full bg-brand-deep text-white py-6 rounded-lg font-black text-sm uppercase tracking-[0.3em] hover:bg-brand-accent transition-all shadow-xl shadow-brand-deep/20"
                   >
                     Sazināties ar ekspertu
                   </button>
                </div>
             </div>
          </div>
        </section>
      </div>
    </div>
  );
};
